import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { compressImageToDataUrl, runDebtImport, type ParsedDebt } from '@/lib/creditImport';
import {
  formatMoney,
  formatDateTime,
  type CreditCustomer,
  type CreditImportBatch,
} from '@/lib/credit';

interface Props {
  customers: CreditCustomer[];
  currency: string;
  importBatches: CreditImportBatch[];
  onCreateCustomer: (data: Partial<CreditCustomer>) => Promise<CreditCustomer>;
  onAddCredit: (customerId: number, amount: number, description?: string) => Promise<{ id: number } | void>;
  onCreateBatch: (input: {
    photoCount: number;
    items: { name: string; amount: number; description?: string }[];
    createdCustomerIds: number[];
    createdTransactionIds: number[];
  }) => Promise<CreditImportBatch>;
  onRevertBatch: (id: number) => Promise<void>;
  onClose: () => void;
  onDone: (created: CreditCustomer[]) => void;
}

type Step = 'upload' | 'review' | 'confirm' | 'creating';

/**
 * ImportDebtsModal — le commerçant photographie les pages de son cahier de dettes.
 * L'IA (système du superadmin) lit les noms + montants, la liste est éditable,
 * puis un clic crée les clients et insère les dettes dans la base.
 * Si un client existe déjà, une confirmation explicite est demandée.
 */
export default function ImportDebtsModal({
  customers, currency, importBatches, onCreateCustomer, onAddCredit, onCreateBatch, onRevertBatch, onClose, onDone,
}: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('upload');
  const [photos, setPhotos] = useState<{ id: string; file: File; preview: string }[]>([]);
  const [debts, setDebts] = useState<ParsedDebt[]>([]);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(0);
  const [revertTarget, setRevertTarget] = useState<CreditImportBatch | null>(null);
  const [reverting, setReverting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .map((file) => ({
        id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        preview: URL.createObjectURL(file),
      }));
    setPhotos((prev) => [...prev, ...next].slice(0, 8));
    setError('');
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const analyse = async () => {
    if (photos.length === 0) return;
    setReading(true);
    setError('');
    try {
      const images = await Promise.all(photos.map((p) => compressImageToDataUrl(p.file)));
      const result = await runDebtImport(images);
      if (result.length === 0) {
        setError(t('crf.ai_read_none', { defaultValue: "L'IA n'a lu aucune dette sur ces photos. Essayez avec une photo plus nette et bien éclairée." }));
      } else {
        setDebts(result);
        setStep('review');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('crf.ai_failed', { defaultValue: "Échec de l'analyse IA" }));
    } finally {
      setReading(false);
    }
  };

  const updateDebt = (index: number, patch: Partial<ParsedDebt>) => {
    setDebts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const removeDebt = (index: number) => {
    setDebts((prev) => prev.filter((_, i) => i !== index));
  };

  const addEmptyDebt = () => {
    setDebts((prev) => [...prev, { name: '', amount: 0, description: '' }]);
  };

  const findExisting = (name: string): CreditCustomer | undefined =>
    customers.find((c) => c.full_name.trim().toLowerCase() === name.trim().toLowerCase());

  const validDebts = debts.filter((d) => d.name.trim() && Number(d.amount) > 0);
  const matched = validDebts.filter((d) => findExisting(d.name));
  const totalImported = validDebts.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

  const startConfirm = () => {
    if (validDebts.length === 0) {
      setError(t('crf.add_at_least_one', { defaultValue: 'Ajoutez au moins une dette avec un nom et un montant.' }));
      return;
    }
    setError('');
    if (matched.length > 0) {
      setStep('confirm');
      return;
    }
    void runImport();
  };

  const runImport = async () => {
    setStep('creating');
    setError('');
    setCreated(0);
    const createdCustomers: CreditCustomer[] = [];
    const createdCustomerIds: number[] = [];
    const createdTransactionIds: number[] = [];
    try {
      for (const debt of validDebts) {
        const existing = findExisting(debt.name);
        let target = existing;
        if (!target) {
          target = await onCreateCustomer({ full_name: debt.name.trim() });
          createdCustomers.push(target);
          createdCustomerIds.push(target.id);
        }
        const res = await onAddCredit(target.id, Number(debt.amount), debt.description || 'Dette reprise du carnet');
        if (res && typeof res.id === 'number' && res.id > 0) createdTransactionIds.push(res.id);
        setCreated((prev) => prev + 1);
      }

      await onCreateBatch({
        photoCount: photos.length,
        items: validDebts.map((d) => ({ name: d.name.trim(), amount: Number(d.amount), description: d.description || '' })),
        createdCustomerIds,
        createdTransactionIds,
      });

      onDone(createdCustomers);
      onClose();
    } catch (e) {
      setStep('review');
      setError(e instanceof Error ? e.message : t('crf.import_failed', { defaultValue: "Échec de l'import" }));
    }
  };

  const doRevert = async () => {
    if (!revertTarget) return;
    setReverting(true);
    setError('');
    try {
      await onRevertBatch(revertTarget.id);
      setRevertTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('crf.revert_failed', { defaultValue: "Échec de l'annulation" }));
    } finally {
      setReverting(false);
    }
  };

  const activeBatches = importBatches.filter((b) => b.status !== 'reverted');

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={step === 'creating' ? undefined : onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-2xl bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-camera-lens-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950">{t('crf.import_title', { defaultValue: 'Importer les dettes par photo' })}</h3>
                <p className="text-[11px] text-foreground-500 truncate">
                  {step === 'upload'
                    ? t('crf.import_sub_upload', { defaultValue: 'Photographiez la page du cahier' })
                    : step === 'confirm'
                      ? t('crf.import_sub_confirm', { defaultValue: 'Confirmation requise' })
                      : t('crf.import_sub_review', { defaultValue: 'Vérifiez puis validez' })}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={step === 'creating'}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0 disabled:opacity-40"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 overflow-y-auto">
            {step === 'upload' && (
              <div className="space-y-4">
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {t('crf.import_desc', { defaultValue: "Prenez une ou plusieurs photos de la page du cahier (noms + montants). L'IA lit ensuite les dettes et vous les présente pour validation. Rien n'est enregistré avant votre confirmation." })}
                </p>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="hidden"
                  onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
                />

                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full border-2 border-dashed border-background-300/60 rounded-xl py-8 flex flex-col items-center gap-2 hover:border-primary-300 hover:bg-primary-50/40 transition-colors cursor-pointer"
                >
                  <span className="w-12 h-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className="ri-camera-line text-2xl"></i>
                  </span>
                  <span className="text-sm font-semibold text-foreground-800">{t('crf.take_photos', { defaultValue: 'Prendre / choisir des photos' })}</span>
                  <span className="text-[11px] text-foreground-400">{t('crf.photos_hint', { defaultValue: "Jusqu'à 8 photos · JPG, PNG" })}</span>
                </button>

                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                    {photos.map((p) => (
                      <div key={p.id} className="relative rounded-lg overflow-hidden border border-background-200/70 aspect-square bg-background-100">
                        <img src={p.preview} alt="Page du carnet" className="w-full h-full object-cover" />
                        <button
                          onClick={() => removePhoto(p.id)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Historique des imports */}
                {importBatches.length > 0 && (
                  <div className="pt-2 border-t border-background-200/70">
                    <h4 className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                      <i className="ri-history-line"></i>{t('crf.import_history', { defaultValue: 'Historique des imports' })}
                    </h4>
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {importBatches.map((b) => {
                        const isReverted = b.status === 'reverted';
                        return (
                          <div key={b.id} className="flex items-center justify-between gap-2 bg-background-100 rounded-lg px-3 py-2">
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-foreground-800 truncate">
                                {b.item_count} dette{b.item_count > 1 ? 's' : ''} · {formatMoney(Number(b.total_amount), currency)}
                              </div>
                              <div className="text-[11px] text-foreground-400 truncate">
                                {formatDateTime(b.created_at)} · {b.photo_count} photo{b.photo_count > 1 ? 's' : ''}
                              </div>
                            </div>
                            {isReverted ? (
                              <span className="text-[11px] font-medium text-foreground-400 whitespace-nowrap px-2 py-1 bg-background-200/60 rounded-full">{t('crf.import_cancelled', { defaultValue: 'Annulé' })}</span>
                            ) : (
                              <button
                                onClick={() => setRevertTarget(b)}
                                className="text-[11px] font-medium text-red-600 hover:text-red-700 whitespace-nowrap px-2.5 py-1 bg-red-50 hover:bg-red-100 rounded-full transition-colors cursor-pointer flex items-center gap-1"
                              >
                                <i className="ri-arrow-go-back-line"></i>{t('crf.cancel_btn', { defaultValue: 'Annuler' })}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {activeBatches.length > 0 && (
                      <p className="text-[11px] text-foreground-400 mt-2">
                        {t('crf.revert_hint', { defaultValue: 'Annuler un import supprime les dettes créées (et les clients créés par cet import).' })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 'review' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-foreground-500">
                    {t('crf.detected_total', { defaultValue: '{count} dette(s) détectée(s) · total {total}', count: debts.length, total: formatMoney(totalImported, currency) })}
                  </p>
                  <button
                    onClick={addEmptyDebt}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>{t('crf.add_line', { defaultValue: 'Ajouter une ligne' })}
                  </button>
                </div>

                <div className="space-y-2">
                  {debts.map((d, i) => {
                    const existing = findExisting(d.name);
                    return (
                      <div key={i} className="bg-background-100 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={d.name}
                            onChange={(e) => updateDebt(i, { name: e.target.value })}
                            placeholder={t('crf.customer_name', { defaultValue: 'Nom du client' })}
                            className="flex-1 h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                          />
                          <div className="relative w-32 flex-shrink-0">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={d.amount || ''}
                              onChange={(e) => updateDebt(i, { amount: parseFloat(e.target.value) || 0 })}
                              placeholder="0"
                              className="w-full h-10 pl-3 pr-12 rounded-md border border-background-200/70 bg-background-50 text-sm font-semibold text-foreground-950 focus:outline-none focus:border-primary-300"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-foreground-400 font-medium">{currency}</span>
                          </div>
                          <button
                            onClick={() => removeDebt(i)}
                            className="w-9 h-9 rounded-md bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                        <input
                          type="text"
                          value={d.description}
                          onChange={(e) => updateDebt(i, { description: e.target.value })}
                          placeholder={t('crf.debt_detail_ph', { defaultValue: 'Détail (ex : 2 sacs de farine, pain…)' })}
                          className="w-full h-9 px-3 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-800 focus:outline-none focus:border-primary-300"
                        />
                        {existing && (
                          <div className="flex items-center gap-1.5 text-[11px] text-accent-700 bg-accent-50 rounded-md px-2.5 py-1.5">
                            <i className="ri-user-follow-line"></i>
                            {t('crf.existing_customer_tag', { defaultValue: 'Client existant — la dette sera ajoutée à son compte' })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {debts.length === 0 && (
                    <p className="text-center text-sm text-foreground-400 py-8">{t('crf.no_debt', { defaultValue: 'Aucune dette. Ajoutez une ligne manuellement.' })}</p>
                  )}
                </div>
              </div>
            )}

            {step === 'confirm' && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 bg-accent-50 border border-accent-200/70 rounded-xl p-4">
                  <span className="w-10 h-10 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-alert-line text-xl"></i>
                  </span>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-foreground-950">{t('crf.confirm_existing_title', { defaultValue: 'Client(s) déjà existant(s)' })}</h4>
                    <p className="text-xs text-foreground-600 mt-1 leading-relaxed">
                      {t('crf.confirm_existing_desc', { defaultValue: "Ces noms existent déjà dans votre carnet. Confirmez-vous l'ajout de ces dettes sur leurs comptes existants ?" })}
                    </p>
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {matched.map((d) => (
                        <span key={d.name} className="px-2.5 py-1 rounded-full bg-background-50 border border-accent-200/70 text-xs font-medium text-accent-800">
                          {d.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-foreground-500">
                  {t('crf.new_clients_count', { defaultValue: '{count} nouveau(x) client(s) seront aussi créés.', count: validDebts.length - matched.length })}
                </p>
              </div>
            )}

            {step === 'creating' && (
              <div className="py-14 flex flex-col items-center gap-3">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
                <p className="text-sm text-foreground-600">{t('crf.creating_progress', { defaultValue: 'Création des clients et des dettes... {done}/{total}', done: created, total: validDebts.length })}</p>
              </div>
            )}

            {error && (
              <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-background-200/70 flex flex-col sm:flex-row gap-2.5 flex-shrink-0">
            {step === 'upload' && (
              <button
                onClick={analyse}
                disabled={photos.length === 0 || reading}
                className="w-full h-12 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reading ? <><i className="ri-loader-4-line animate-spin"></i>{t('crf.reading_ai', { defaultValue: "Lecture par l'IA..." })}</> : <><i className="ri-sparkling-line"></i>{t('crf.analyse_ai', { defaultValue: "Analyser avec l'IA" })}</>}
              </button>
            )}
            {step === 'review' && (
              <>
                <button
                  onClick={() => setStep('upload')}
                  className="w-full sm:w-auto h-12 px-5 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('crf.back', { defaultValue: 'Retour' })}
                </button>
                <button
                  onClick={startConfirm}
                  disabled={validDebts.length === 0}
                  className="flex-1 h-12 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-check-line"></i>
                  {t('crf.validate_create', { defaultValue: 'Valider et créer' })} ({validDebts.length})
                </button>
              </>
            )}
            {step === 'confirm' && (
              <>
                <button
                  onClick={() => setStep('review')}
                  className="w-full sm:w-auto h-12 px-5 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('crf.cancel_btn', { defaultValue: 'Annuler' })}
                </button>
                <button
                  onClick={runImport}
                  className="flex-1 h-12 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-check-double-line"></i>
                  {t('crf.confirm_add_existing', { defaultValue: 'Confirmer et ajouter aux comptes existants' })}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation d'annulation d'import */}
      {revertTarget && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => (reverting ? undefined : setRevertTarget(null))} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-arrow-go-back-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">{t('crf.revert_question', { defaultValue: 'Annuler cet import ?' })}</h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {t('crf.revert_desc', { defaultValue: '{count} dette(s) supprimée(s).', count: revertTarget.item_count })}
                  {revertTarget.created_customer_ids?.length > 0 ? ` · ${revertTarget.created_customer_ids.length}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setRevertTarget(null)} disabled={reverting} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40">{t('crf.cancel_btn', { defaultValue: 'Annuler' })}</button>
              <button onClick={doRevert} disabled={reverting} className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white whitespace-nowrap hover:bg-red-600 transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-50">
                {reverting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}{t('crf.confirm', { defaultValue: 'Confirmer' })}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}